import React from "react";
import ReactDOM from "react-dom/client";
import "./styling/App.css";
import "@fontsource/inter/variable-full.css";
import {createBrowserRouter, RouterProvider, ScrollRestoration} from "react-router-dom";
import DutchMessages from "./messages/nl-NL";
import {IntlProvider} from "react-intl";
import {NotificationProvider, ResponsiveProvider, ThemeProvider} from "peggirkit";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import HydrationFallback from "@/context/authentication/hydration-fallback";
import {userLoader} from "@/context/authentication/user-loader";
import AuthStateLayout from "@/context/authentication/auth-state-layout";
import Error from "@pages/unauthenticated/Error";
import IndexRedirect from "./context/authentication/index-redirect";
import SignIn from "@components/pages/unauthenticated/SignIn";
import RequestResetPassword from "@pages/unauthenticated/reset/RequestResetPassword";
import ConfirmResetPassword from "@pages/unauthenticated/reset/ConfirmResetPassword";
import AuthenticatedLayout from "./context/authentication/authenticated-layout";
import Dashboard from "@components/pages/authenticated/admin/dashboard/Dashboard";
import SystemInfo from "@components/pages/authenticated/admin/system/SystemInfo";
import DeliveryAreas from "@components/pages/authenticated/admin/deliveryAreas/DeliveryAreas";
import Location from "@components/pages/authenticated/admin/location/Location";
import Account from "@components/pages/authenticated/admin/account/Account";
import CartTimeline from "@pages/authenticated/admin/reports/CartTimeline";
import PosUpcomingOrders from "@pages/authenticated/pos/dashboard/PosUpcomingOrders";
import JobApplications from "@components/pages/authenticated/admin/jobApplications/JobApplications";
import router, {
    editEmployee,
    editFeaturedProduct,
    editJobApplicationPart,
    editLoyaltyReward,
    posEditOrderPart,
    posOrderPart,
    posOrders,
    posOrdersPart,
    posUpcomingOrdersPart
} from "./routing/Router";
import BusynessManagement from "@components/pages/authenticated/admin/busynessManagement/BusynessManagement";
import JobOpenings from "@components/pages/authenticated/admin/jobOpenings/JobOpenings";
import JobApplication from "@components/pages/authenticated/admin/jobApplications/JobApplication";
import Employees from "@components/pages/authenticated/admin/employees/Employees";
import Employee from "@components/pages/authenticated/admin/employees/Employee";
import FeaturedProducts from "@components/pages/authenticated/admin/products/FeaturedProducts";
import FeaturedProduct from "@components/pages/authenticated/admin/products/FeaturedProduct";
import LoyaltyRewards from "@components/pages/authenticated/admin/loyaltyRewards/LoyaltyRewards";
import LoyaltyReward from "@components/pages/authenticated/admin/loyaltyRewards/LoyaltyReward";
import Reports from "@pages/authenticated/admin/reports/Reports";
import Timesheet from "@pages/authenticated/admin/reports/Timesheet";
import TooltipProvider from "@widgets/tooltip/TooltipProvider";
import PosDashboardHome from "@pages/authenticated/pos/dashboard/PosDashboardHome";
import OnlyUnauthenticatedRedirect from "@/context/authentication/only-unauthenticated-redirect";
import EmployeeEnvironment from "@pages/employees/EmployeeEnvironment";
import PosOrders from "@pages/authenticated/pos/dashboard/PosOrders";
import PosLayout from "@widgets/pos/layout/PosLayout";
import {Navigate} from "react-router";
import PosOrder from "@pages/authenticated/pos/dashboard/PosOrder";
import DayReport from "@pages/authenticated/admin/reports/DayReport";
import Menu from "@pages/authenticated/admin/menu/Menu";
import OptionGroups from "@pages/authenticated/admin/menu/OptionGroups";

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
        },
    },
});

const routes = createBrowserRouter([{
    id: "auth-root",
    hydrateFallbackElement: (
        <IntlProvider
            locale="nl-NL"
            messages={DutchMessages}
        >
            <HydrationFallback/>
        </IntlProvider>),
    loader: userLoader,
    // `shouldRevalidate` is `false`, so the loader doesn't get
    // triggered after page change or url params change, that way we
    // only initially load it:
    shouldRevalidate: () => false,
    element: (
        <IntlProvider
            locale="nl-NL"
            messages={DutchMessages}
        >
            <QueryClientProvider client={queryClient}>
                <ThemeProvider>
                    <ResponsiveProvider>
                        <NotificationProvider
                            position={"bottom-center"}
                            breakpoints={{
                                md: {
                                    position: "top-right",
                                },
                            }}
                        >
                            <TooltipProvider>
                                <AuthStateLayout/>
                            </TooltipProvider>
                        </NotificationProvider>
                    </ResponsiveProvider>
                </ThemeProvider>
            </QueryClientProvider>
            <ScrollRestoration/>
        </IntlProvider>
    ),
    errorElement: (
        <IntlProvider
            locale="nl-NL"
            messages={DutchMessages}
        >
            <ThemeProvider>
                <ResponsiveProvider>
                    <Error/>
                </ResponsiveProvider>
            </ThemeProvider>
        </IntlProvider>
    ),
    children: [
        {
            path: "/inklokken",
            element: <Navigate to={router.employeeEnvironment.absolutePath()} replace/>,
        },
        {
            path: router.employeeEnvironment.relativePath,
            element: <EmployeeEnvironment/>,
        },
        {
            index: true,
            element: <IndexRedirect/>, // Either redirects to sign in or dashboard home
        },
        {

            path: router.auth.relativePath,
            element: <OnlyUnauthenticatedRedirect/>, // Redirects to index when already authenticated
            children: [
                {
                    path: router.signIn.relativePath,
                    element: <SignIn/>,
                },
                {
                    path: router.reset.relativePath,
                    children: [
                        {
                            index: true,
                            element: <RequestResetPassword/>,
                        },
                        {
                            path: router.confirmReset.relativePath,
                            element: <ConfirmResetPassword/>,
                        },
                    ]
                },
            ],
        },
        {
            element: <AuthenticatedLayout/>, // Redirects to sign in when not authenticated
            children: [
                // Admin:
                {
                    path: router.adminHome.relativePath,
                    children: [
                        {
                            index: true,
                            element: <Dashboard/>,
                        },
                        {
                            path: router.systemInfo.relativePath,
                            element: <SystemInfo/>,
                        },
                        {
                            path: router.account.relativePath,
                            element: <Account/>,
                        },
                        {
                            path: router.deliveryAreas.relativePath,
                            element: <DeliveryAreas/>,
                        },
                        {
                            path: router.location.relativePath,
                            element: <Location/>,
                        },
                        {
                            path: router.busynessManagement.relativePath,
                            element: <BusynessManagement/>,
                        },
                        {
                            path: router.jobOpenings.relativePath,
                            element: <JobOpenings/>,
                        },
                        {
                            path: router.menu.relativePath,
                            children: [
                                {
                                    index: true,
                                    element: <Menu/>,
                                },
                                {
                                    path: router.optionGroups.relativePath,
                                    element: <OptionGroups/>,
                                },
                            ],
                        },
                        {
                            path: router.jobApplications.relativePath,
                            children: [
                                {
                                    index: true,
                                    element: <JobApplications/>,
                                },
                                {
                                    path: editJobApplicationPart,
                                    element: <JobApplication/>,
                                },
                            ],
                        },
                        {
                            path: router.employees.relativePath,
                            children: [
                                {
                                    index: true,
                                    element: <Employees/>,
                                },
                                {
                                    path: router.newEmployee.relativePath,
                                    element: <Employee/>,
                                },
                                {
                                    path: editEmployee().relativePath,
                                    element: <Employee/>,
                                },
                            ],
                        },
                        {
                            path: router.featuredProducts.relativePath,
                            children: [
                                {
                                    index: true,
                                    element: <FeaturedProducts/>,
                                },
                                {
                                    path: router.newFeaturedProduct.relativePath,
                                    element: <FeaturedProduct/>,
                                },
                                {
                                    path: editFeaturedProduct().relativePath,
                                    element: <FeaturedProduct/>,
                                },
                            ],
                        },
                        {
                            path: router.loyaltyRewards.relativePath,
                            children: [
                                {
                                    index: true,
                                    element: <LoyaltyRewards/>,
                                },
                                {
                                    path: router.newLoyaltyReward.relativePath,
                                    element: <LoyaltyReward/>,
                                },
                                {
                                    path: editLoyaltyReward().relativePath,
                                    element: <LoyaltyReward/>,
                                },
                            ],
                        },
                        {
                            path: router.reports.relativePath,
                            children: [
                                {
                                    index: true,
                                    element: <Reports/>,
                                },
                                {
                                    path: router.dayReport.relativePath,
                                    element: <DayReport/>,
                                },
                                {
                                    path: router.timesheet.relativePath,
                                    element: <Timesheet/>,
                                },
                                {
                                    path: router.cartTimeline.relativePath,
                                    element: <CartTimeline/>,
                                },
                            ],
                        },
                    ],
                },

                // POS:
                {
                    path: router.posAdminDashboardHome.relativePath,
                    children: [
                        {
                            index: true,
                            element: <PosDashboardHome/>,
                        },
                        {
                            path: router.posDashboardHome.relativePath,
                            element: <PosLayout/>,
                            children: [
                                {
                                    index: true,
                                    element: <Navigate to={posOrders().absolutePath()} replace/>,
                                },
                                {
                                    path: `${posOrdersPart}/${posOrderPart}`,
                                    children: [
                                        {
                                            index: true,
                                            element: <PosOrders/>,
                                        },
                                    ],
                                },
                                {
                                    path: posUpcomingOrdersPart,
                                    element: <PosUpcomingOrders/>,
                                },
                                {
                                    path: `${posEditOrderPart}/${posOrderPart}`,
                                    children: [
                                        {
                                            index: true,
                                            element: <PosOrder/>,
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    ],
}]);

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
    <RouterProvider router={routes}/>
);